/**
 * Digital Company — rotation math for layout pieces.
 *
 * Stored rectangles stay axis-aligned in local space. `rotation` is degrees
 * clockwise around the piece centre (canvas Y-down, matching `ctx.rotate`).
 * 0° keeps the default "front" toward the bottom of the map. Legacy
 * `facing` values map onto 90° steps so older saves still orient desks.
 */

export type CardinalFacing = 'up' | 'down' | 'left' | 'right';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Rotatable {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  rotation?: number;
  facing?: CardinalFacing;
}

export function normalizeDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let n = deg % 360;
  if (n < 0) n += 360;
  if (n < 0.05 || n > 359.95) return 0;
  return n;
}

export function facingToRotation(facing?: CardinalFacing): number {
  switch (facing) {
    case 'right':
      return 90;
    case 'up':
      return 180;
    case 'left':
      return 270;
    default:
      return 0;
  }
}

export function rotationToFacing(deg: number): CardinalFacing {
  const n = normalizeDeg(deg);
  if (n >= 315 || n < 45) return 'down';
  if (n < 135) return 'right';
  if (n < 225) return 'up';
  return 'left';
}

/** Clockwise degrees for a piece. Explicit `rotation` wins over `facing`. */
export function pieceRotation(piece: Rotatable): number {
  if (typeof piece.rotation === 'number' && Number.isFinite(piece.rotation)) {
    return normalizeDeg(piece.rotation);
  }
  return facingToRotation(piece.facing);
}

export function rectCenter(r: Rect): { x: number; y: number } {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

export function rotatePoint(
  x: number,
  y: number,
  cx: number,
  cy: number,
  deg: number,
): { x: number; y: number } {
  if (!deg) return { x, y };
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - cx;
  const dy = y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

export function rotatedAABB(r: Rect, deg: number): Rect {
  const n = normalizeDeg(deg);
  if (!n) return { x: r.x, y: r.y, w: r.w, h: r.h };
  const c = rectCenter(r);
  const corners = [
    rotatePoint(r.x, r.y, c.x, c.y, n),
    rotatePoint(r.x + r.w, r.y, c.x, c.y, n),
    rotatePoint(r.x + r.w, r.y + r.h, c.x, c.y, n),
    rotatePoint(r.x, r.y + r.h, c.x, c.y, n),
  ];
  let minX = corners[0].x;
  let maxX = corners[0].x;
  let minY = corners[0].y;
  let maxY = corners[0].y;
  for (let i = 1; i < 4; i++) {
    const p = corners[i];
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function visualBounds(piece: Rect): Rect {
  return rotatedAABB(piece, pieceRotation(piece));
}

export function pointInRotatedRect(r: Rect, deg: number, x: number, y: number, pad = 0): boolean {
  const c = rectCenter(r);
  const local = rotatePoint(x, y, c.x, c.y, -deg);
  return (
    local.x >= r.x - pad &&
    local.x <= r.x + r.w + pad &&
    local.y >= r.y - pad &&
    local.y <= r.y + r.h + pad
  );
}

export function snapRotation(deg: number, fine: boolean): number {
  const step = fine ? 1 : 15;
  return normalizeDeg(Math.round(deg / step) * step);
}

export function angleDeg(cx: number, cy: number, x: number, y: number): number {
  return (Math.atan2(y - cy, x - cx) * 180) / Math.PI;
}

export function worldDeltaToLocal(dx: number, dy: number, deg: number): { dx: number; dy: number } {
  const p = rotatePoint(dx, dy, 0, 0, -deg);
  return { dx: p.x, dy: p.y };
}
